import * as React from 'react';
import {
  useRef,
  useEffect,
  useLayoutEffect,
  useState,
  useCallback,
} from 'react';
import {
  SortableContext,
  verticalListSortingStrategy,
  type SortingStrategy,
} from '@dnd-kit/sortable';
import { useDroppable } from '@dnd-kit/core';
import { useVirtualizer } from '@tanstack/react-virtual';

import { Box } from '../../components/ui/box';
import { Text } from '../../components/ui/text';
import { Icon } from '../../components/ui/icon';
import { Button } from '../../components/ui/button';
import {
  Menu,
  MenuTrigger,
  MenuPopup,
  MenuGroup,
  MenuGroupLabel,
  MenuItem,
  MenuSeparator,
} from '../../components/ui/menu';
import { useIsMobile } from '../../hooks/use-media-query';
import { useIsTouchDevice } from '../../hooks/use-is-touch-device';
import { useWindowedPages } from '../../hooks/use-windowed-pages';
import { cn } from '../../lib/utils';

import { KanbanColumnItemsProvider, useKanbanDrag } from './kanban-context';
import { shouldShowDropDisabledVeil } from './kanban-drop';
import { cardDndId } from './kanban-ids';
import { KanbanCard } from './kanban-card';
import { KanbanColumnSkeleton, KanbanCardSkeleton } from './kanban-skeleton';
import type { Id, KanbanColumnProps } from './types';
import type { Omittable } from "../../lib/props";

export const COLUMN_WIDTH = 340;
// Never moves a card: while drops are disabled, a drag lifts the card without
// the column shifting siblings to open an insertion gap.
const NO_REORDER_STRATEGY: SortingStrategy = () => null;

const COLLAPSED_WIDTH = 40;
export const COLLAPSED_HEIGHT = 360;
const MOBILE_COLUMN_PEEK = 4;
// The px value of the "sm" spacing step (gap-2 / p-2) — participates in the
// scroll-max and mobile-width math below, so it must stay in sync with the
// Box "sm" token.
const SPACING_SM = 8;
export const MOBILE_BOARD_PADDING = SPACING_SM;
export const CARD_HEIGHT_BASE = 109; // card + 8px row gap
const DEFAULT_PAGE_SIZE = 50;

/**
 * Live window width. The board only needs it for `columnOuterWidth` (mobile
 * columns size to the viewport), so a resize-subscribed `useSyncExternalStore`
 * readout is enough — no context provider.
 */
export function useWindowWidth(): number {
  return React.useSyncExternalStore(
    subscribeToWindowResize,
    () => window.innerWidth,
    () => 1024,
  );
}

function subscribeToWindowResize(callback: () => void) {
  window.addEventListener('resize', callback);
  return () => window.removeEventListener('resize', callback);
}

/**
 * Outer width of a board column. Single source of truth shared by the column
 * itself and the board's lazy-mount placeholder, which must reserve the exact
 * same width so scroll geometry (and mobile scroll-snap) don't shift when the
 * real column swaps in for its placeholder.
 */
export function columnOuterWidth(
  opts: Omittable<{
    collapsed: boolean;
    columnWidth?: number;
    isMobile: boolean;
    windowWidth: number;
  }>,
): number {
  const { collapsed, columnWidth, isMobile, windowWidth } = opts;
  if (collapsed) return COLLAPSED_WIDTH;
  if (isMobile) {
    // Viewport − left padding − inter-column gap − peek (a sliver of the next
    // column that signals horizontal scrollability). Clamped so narrow devices
    // can't collapse below a usable width.
    return Math.max(
      240,
      windowWidth - MOBILE_BOARD_PADDING - SPACING_SM - MOBILE_COLUMN_PEEK,
    );
  }
  return columnWidth ?? COLUMN_WIDTH;
}

// Whether the OS uses classic (gutter-consuming) scrollbars vs overlay ones.
// Stable per session; lets classic reserve a gutter under the rounded edge
// while overlay keeps corner padding, so the column never flickers either way.
//
// Measured on FIRST CALL, not at module scope: this file is reachable from the
// package barrel, so probing the DOM at import time throws `document is not
// defined` in every non-DOM consumer (SSR, a node-environment test suite)
// before a single component mounts. Nothing in this package may touch
// `document` at import time.
let gutterScrollbar: boolean | undefined;

function hasGutterScrollbar(): boolean {
  if (gutterScrollbar !== undefined) return gutterScrollbar;
  if (typeof document === "undefined") return false;
  const probe = document.createElement("div");
  probe.style.cssText =
    "position:absolute;top:-9999px;width:50px;height:50px;overflow:scroll;visibility:hidden";
  document.body.appendChild(probe);
  gutterScrollbar = probe.offsetWidth > probe.clientWidth;
  document.body.removeChild(probe);
  return gutterScrollbar;
}

function KanbanColumnInner<TItem, TColumn>({
  column,
  data,
}: KanbanColumnProps<TItem, TColumn>) {
  const {
    activeItem,
    activeColumnId,
    overColumnId,
    getItemId,
    getSortValue,
    getColumnId,
    estimateItemSize,
    collapsedColumnIds,
    onToggleCollapse,
    renderColumnHeader,
    renderCollapsedColumn,
    renderColumnFooter,
    columnMenuItems,
    columnWidth,
    isDropDisabled,
    dropDisabledOverlay,
  } = useKanbanDrag<TItem, TColumn>();

  const columnId = getColumnId(column);
  // Full server total — drives both the virtualizer count and the header count;
  // the column virtualizes over every row, loaded or not.
  const count = data.total;
  const pageSize = data.pageSize ?? DEFAULT_PAGE_SIZE;

  const collapsed = collapsedColumnIds.has(columnId);
  const isSourceColumn = activeColumnId === columnId;
  const isDraggingOver = overColumnId === columnId;

  const scrollContainerRef = useRef<HTMLDivElement | null>(null);
  const expandedContainerRef = useRef<HTMLDivElement | null>(null);
  const headerRef = useRef<HTMLDivElement | null>(null);
  const footerRef = useRef<HTMLDivElement | null>(null);
  const [autoExpanded, setAutoExpanded] = useState(false);

  const isMobile = useIsMobile();
  const windowWidth = useWindowWidth();
  const isTouchDevice = useIsTouchDevice();

  const dragOverTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const wasCollapsedRef = useRef(collapsed);

  // Auto-expand collapsed column on drag-over (250ms delay); skipped while
  // drops are disabled since there's nothing to drop into.
  useEffect(() => {
    if (collapsed && isDraggingOver && !isDropDisabled) {
      dragOverTimerRef.current = setTimeout(() => {
        setAutoExpanded(true);
      }, 250);
    } else {
      if (dragOverTimerRef.current) clearTimeout(dragOverTimerRef.current);
      if (!isDraggingOver) setAutoExpanded(false);
    }
    return () => {
      if (dragOverTimerRef.current) clearTimeout(dragOverTimerRef.current);
    };
  }, [collapsed, isDraggingOver, isDropDisabled]);

  const isExpanded = !collapsed || autoExpanded;

  const handleToggleCollapse = useCallback(
    () => onToggleCollapse(columnId),
    [onToggleCollapse, columnId],
  );

  // Outer width must match the placeholder the board reserves for a not-yet-
  // mounted column, so both read from the shared `columnOuterWidth` helper.
  const expandedWidth = columnOuterWidth({
    collapsed: false,
    columnWidth,
    isMobile,
    windowWidth,
  });

  // The virtualizer is created BEFORE the windowing hook (which needs the
  // instance), so its `estimateSize` reads the page resolver through a ref;
  // `getItem` is stable so the virtualizer never re-inits.
  const getItemRef = useRef<(index: number) => TItem | undefined>(
    () => undefined,
  );
  const estimateSize = useCallback(
    (index: number) => estimateItemSize(getItemRef.current(index), index),
    [estimateItemSize],
  );

  const virtualizer = useVirtualizer({
    count,
    getScrollElement: () => scrollContainerRef.current,
    estimateSize,
    overscan: 5,
  });

  const { getItem, cacheVersion } = useWindowedPages<TItem>({
    total: count,
    pageSize,
    pageItems: data.items,
    dataOffset: data.dataOffset,
    variablesKey: data.variablesKey,
    fetchPage: data.fetchPage,
    virtualizer,
    scrollElementRef:
      scrollContainerRef as React.RefObject<HTMLElement | null>,
    enabled: isExpanded && count > 0,
  });
  getItemRef.current = getItem;
  // Read so the lint dep graph (and downstream memos) refresh when a page
  // loads; the value itself is incidental.
  void cacheVersion;

  // The column's full ordered slot list, materialized only when a shift+click
  // asks for it (via KanbanColumnItemsProvider). Reads through refs so the
  // function identity never changes — the provider must not re-render cards.
  const countRef = useRef(count);
  countRef.current = count;
  const getOrderedItems = useCallback((): readonly (TItem | undefined)[] => {
    const out: (TItem | undefined)[] = [];
    for (let i = 0; i < countRef.current; i++) out.push(getItemRef.current(i));
    return out;
  }, []);

  const virtualItems = virtualizer.getVirtualItems();

  // Cross-column drag overlay. The committed drop reads the OVER-card's baked
  // neighbor data in handleDragEnd, never the active card's index, so the
  // virtualizer count stays pinned at `total`. These flags only shape the
  // visual: a destination column opens a gap, a source column closes the slot.
  const activeId = activeItem != null ? getItemId(activeItem) : null;
  // The active card's OWN dnd id (scoped to the column it was grabbed from).
  // The destination phantom must register under THIS id — not the destination's
  // — so dnd-kit treats it as the active node (see KanbanCard `dndId`).
  const activeDndId =
    activeId != null && activeColumnId != null
      ? cardDndId(activeColumnId, activeId)
      : null;

  const loadedWindowHasActive =
    activeId != null &&
    virtualItems.some((vi) => {
      const it = getItem(vi.index);
      return it != null && getItemId(it) === activeId;
    });

  // Append the active card to this column when it's dragged over and not yet in
  // the loaded window. Suppressed while drops are disabled (no gap to open).
  const appendActive =
    !isDropDisabled &&
    activeItem != null &&
    !isSourceColumn &&
    isDraggingOver &&
    !loadedWindowHasActive;
  // Collapse the active card's slot in its source column once the pointer
  // leaves (the card now lives in the drag overlay). Suppressed while drops are
  // disabled, so the card stays put and only the overlay floats.
  const removeActive =
    !isDropDisabled && activeItem != null && isSourceColumn && !isDraggingOver;

  // The loaded window's ids in order, with the drag overlay applied;
  // verticalListSortingStrategy animates these.
  const sortableIds = React.useMemo(() => {
    const ids: Id[] = [];
    for (const vi of virtualItems) {
      const it = getItem(vi.index);
      if (it == null) continue;
      const itemId = getItemId(it);
      if (removeActive && itemId === activeId) continue;
      ids.push(cardDndId(columnId, itemId));
    }
    if (appendActive && activeDndId != null) ids.push(activeDndId);
    return ids;
    // eslint-disable-next-line react-hooks/exhaustive-deps -- cacheVersion drives the window contents
  }, [virtualItems, getItem, getItemId, removeActive, appendActive, activeId, activeDndId, cacheVersion, columnId]);

  // Tail sort value for end-of-column drops; resolves once the final page
  // loads. Until then a drop on the empty area midpoints against no lower bound
  // (a rare edge that only fires before the tail page lands).
  const tailItem = count > 0 ? getItem(count - 1) : undefined;
  const lastSortValue = tailItem ? getSortValue(tailItem) : undefined;

  // Droppable target for the whole column (empty columns / dropping at end).
  // Disabled board-wide turns it off so dnd-kit reports no over-state.
  const { setNodeRef: setDroppableRef } = useDroppable({
    id: `column-${columnId}`,
    data: { kind: 'column', columnId, lastSortValue },
    disabled: isDropDisabled,
  });

  // Veil over the column body while a card is in flight and drops are disabled,
  // explaining why this grouping can't be reordered.
  const showDropDisabledOverlay = shouldShowDropDisabledVeil({
    isDropDisabled,
    hasActiveItem: activeItem != null,
  });

  // Scroll-container max height from the board row's available space, so the
  // column sizes to content and only scrolls when cards exceed the screen.
  const [scrollMaxHeight, setScrollMaxHeight] = useState<number | undefined>(
    undefined,
  );
  const [canScrollUp, setCanScrollUp] = useState(false);
  const [canScrollDown, setCanScrollDown] = useState(false);

  const updateScrollEdges = useCallback(() => {
    const el = scrollContainerRef.current;
    if (!el) return;
    setCanScrollUp(el.scrollTop > 0);
    setCanScrollDown(el.scrollTop + el.clientHeight < el.scrollHeight - 1);
  }, []);

  const measureScrollMax = useCallback(() => {
    const col = expandedContainerRef.current;
    if (!col) return;
    const boardRow = col.parentElement;
    if (!boardRow) return;

    const boardStyle = getComputedStyle(boardRow);
    const available =
      boardRow.clientHeight -
      parseFloat(boardStyle.paddingTop) -
      parseFloat(boardStyle.paddingBottom);

    const headerH = headerRef.current?.offsetHeight ?? 0;
    const footerH = footerRef.current?.offsetHeight ?? 0;

    const maxH =
      available - headerH - SPACING_SM - footerH - SPACING_SM;
    setScrollMaxHeight(maxH > 0 ? maxH : undefined);
  }, []);

  // Animate the column expanding from collapsed → expanded.
  useEffect(() => {
    if (isExpanded && wasCollapsedRef.current && expandedContainerRef.current) {
      expandedContainerRef.current.animate(
        [
          { opacity: 0, transform: 'translateX(-20px)' },
          { opacity: 1, transform: 'translateX(0)' },
        ],
        { duration: 250, easing: 'cubic-bezier(0.16, 1, 0.3, 1)' },
      );
    }
    wasCollapsedRef.current = !isExpanded;
  }, [isExpanded]);

  // Measure before paint so the first frame already has maxHeight applied;
  // otherwise it paints full-height, then shifts when maxHeight + gutter
  // padding flip on the next frame.
  useLayoutEffect(() => {
    if (!isExpanded) return;
    const col = expandedContainerRef.current;
    const boardRow = col?.parentElement;
    if (!boardRow) return;
    measureScrollMax();
    const observer = new ResizeObserver(measureScrollMax);
    observer.observe(boardRow);
    return () => observer.disconnect();
  }, [isExpanded, measureScrollMax]);

  // Scroll listener for the top/bottom fades. Page fetching is owned by
  // `useWindowedPages` (its own listener on the same element).
  useEffect(() => {
    if (!isExpanded) return;
    const el = scrollContainerRef.current;
    if (!el) return;
    const onScroll = () => updateScrollEdges();
    el.addEventListener('scroll', onScroll, { passive: true });
    return () => el.removeEventListener('scroll', onScroll);
  }, [isExpanded, updateScrollEdges]);

  // Refresh fade flags without waiting for a scroll event, since total size
  // grows as rows measure, maxHeight changes on resize, and pages load.
  const virtualTotalSize = virtualizer.getTotalSize();
  useLayoutEffect(() => {
    if (!isExpanded) return;
    updateScrollEdges();
  }, [isExpanded, scrollMaxHeight, count, virtualTotalSize, updateScrollEdges]);

  // Catch container resizes that don't flow through scrollMaxHeight (browser
  // zoom, devtools docking).
  useLayoutEffect(() => {
    if (!isExpanded) return;
    const el = scrollContainerRef.current;
    if (!el) return;
    const observer = new ResizeObserver(updateScrollEdges);
    observer.observe(el);
    return () => observer.disconnect();
  }, [isExpanded, updateScrollEdges]);

  // Self-hide once loaded and empty (the board's opt-in "hide empty columns").
  // Guarded on !loading so it doesn't flash out before its first page lands.
  // A self-hide rather than a board-level filter keeps the adapter mounted and
  // its query alive.
  if (data.hideWhenEmpty && !data.loading && count === 0) {
    return null;
  }

  if (!isExpanded) {
    return (
      <Box
        as="button"
        ref={setDroppableRef}
        direction="column"
        align="center"
        justify="between"
        rounded="lg"
        // Same fill as the expanded column, so collapsing a group changes its
        // width and nothing about how high it reads.
        background="muted"
        paddingY="sm"
        width={COLLAPSED_WIDTH}
        height={COLLAPSED_HEIGHT}
        shrink={false}
        className={isMobile ? 'snap-start' : undefined}
        onClick={handleToggleCollapse}
      >
        {renderCollapsedColumn?.({
          column,
          count,
          collapsed: true,
          onToggleCollapse: handleToggleCollapse,
        })}
      </Box>
    );
  }

  const headerContent = renderColumnHeader?.({
    column,
    count,
    collapsed: false,
    onToggleCollapse: handleToggleCollapse,
  });
  const menuExtras = columnMenuItems?.(column);
  const footer = renderColumnFooter?.(column);
  const hasFooter = !!footer;

  const showSkeletonColumn = data.loading && count === 0;
  const hasBody = count > 0;

  const expandedBody = (
    <Box alignSelf="start" group="col"
      ref={(node: HTMLDivElement | null) => {
        setDroppableRef(node);
        expandedContainerRef.current = node;
      }}
      direction="column"
      gap="sm"
      width={expandedWidth}
      shrink={false}
      // group/col: column-scoped hover group — header controls fade in on
      // column hover; touch devices (no hover) keeps them always visible.
      className={cn('', isMobile && 'snap-start')}
    >
      {/* Column header */}
      <Box
        ref={headerRef}
        direction="row"
        align="center"
        justify="between"
        rounded="lg"
        // Header and card-list area are two separate slabs on the SAME fill,
        // split by the column's `gap` — not a header stacked on a body. The
        // column is a substrate, not a surface: the cards resting on it
        // are `card`, and the column has to stay darker than them or they
        // vanish into it.
        background="muted"
        paddingX="sm"
        paddingY="sm"
        shrink={false}
        overflow="hidden"
      >
        <Box direction="row" align="center" gap="xs" flex={1} shrink>
          {headerContent}
        </Box>

        {/* Collapse / overflow — revealed on column hover (desktop); always
            visible on touch devices. On mobile the standalone collapse button is
            dropped — its action lives in the overflow menu instead. */}
        <Box
          direction="row"
          align="center"
          gap="xs"
          className={
            isTouchDevice
              ? undefined
              : 'opacity-0 group-hover/col:opacity-100 focus-within:opacity-100'
          }
        >
          {!isMobile ? (
            <Button
              variant="ghost"
              size="icon-sm"
              onClick={handleToggleCollapse}
              aria-label="Collapse group"
            >
              <Icon name="arrow-left-to-line" size="md" />
            </Button>
          ) : null}

          <Menu>
            <MenuTrigger
              render={
                <Button
                  variant="ghost"
                  size="icon-sm"
                  aria-label="Group options"
                />
              }
            >
              <Icon name="more-horizontal" size="md" />
            </MenuTrigger>
            <MenuPopup align="start" className="min-w-[260px]">
              {/* MenuGroupLabel reads its labelled set from MenuGroup context
                  and throws without one, so the label and the items it names
                  are one group — not a bare heading dropped into the popup. */}
              <MenuGroup>
                <MenuGroupLabel>Group options</MenuGroupLabel>
                <MenuItem onClick={handleToggleCollapse}>
                  <Icon name="arrow-left-to-line" size="md" />
                  <Text>Collapse group</Text>
                </MenuItem>
              </MenuGroup>
              {menuExtras ? (
                <>
                  <MenuSeparator />
                  {menuExtras}
                </>
              ) : null}
            </MenuPopup>
          </Menu>
        </Box>
      </Box>

      {/* Card list area */}
      <Box
        position="relative"
        direction="column"
        rounded="lg"
        // The substrate the cards rest on. The scroll fades and the
        // drop-disabled veil below are mixed against this same fill, so all
        // three move together if the column is retuned.
        background="muted"
        shrink
        minHeight={0}
        overflow="hidden"
        // Drag-over ring — an inset shadow, as a static class.
        className={
          isDraggingOver && !isDropDisabled
            ? 'shadow-[inset_0_0_0_1px_color-mix(in_oklab,var(--primary)_50%,transparent)]'
            : undefined
        }
      >
        {/* Scroll viewport — fade overlays attach here so they sit at the
            scroll area's edges, not below the footer button. */}
        <Box position="relative" shrink minHeight={0} overflow="hidden">
          <SortableContext
            items={sortableIds}
            strategy={isDropDisabled ? NO_REORDER_STRATEGY : verticalListSortingStrategy}
          >
            <Box overflowY="auto"
              ref={scrollContainerRef}
              paddingLeft="sm"
              paddingRight={hasGutterScrollbar() ? undefined : 'sm'}
              paddingTop={hasBody || showSkeletonColumn ? 'sm' : undefined}
              // Without a footer the scroll area is last in the column; mirror
              // the top padding so the last card has equal breathing room.
              paddingBottom={
                !hasFooter && (hasBody || showSkeletonColumn) ? 'sm' : undefined
              }
              maxHeight={scrollMaxHeight}
              // Classic scrollbars reserve a stable gutter tucked under the
              // rounded edge; overlay scrollbars keep the corner padding.
              className={cn(
                '',
                hasGutterScrollbar() && '-mr-[0.2rem] [scrollbar-gutter:stable]',
              )}
              // Freeze native touch-scroll while a card is in flight (dynamic —
              // stays inline).
              style={{ touchAction: activeItem ? 'none' : undefined }}
            >
              {showSkeletonColumn ? (
                <KanbanColumnSkeleton />
              ) : (
                hasBody && (
                  <Box
                    fill="width"
                    position="relative"
                    height={virtualizer.getTotalSize()}
                  >
                    <Box
                      fill="width"
                      position="absolute" className="top-0 left-0"
                      style={{
                        transform: `translateY(${virtualItems[0]?.start ?? 0}px)`,
                      }}
                    >
                      {virtualItems.map((virtualRow) => {
                        const item = getItem(virtualRow.index);
                        const prev = getItem(virtualRow.index - 1);
                        const next = getItem(virtualRow.index + 1);
                        const isActiveAway =
                          removeActive &&
                          item != null &&
                          getItemId(item) === activeId;
                        return (
                          <Box
                            key={virtualRow.key}
                            ref={virtualizer.measureElement}
                            paddingBottom={
                              virtualRow.index < count - 1 ? 'sm' : undefined
                            }
                            data-index={virtualRow.index}
                          >
                            {item == null ? (
                              <KanbanCardSkeleton
                                height={estimateItemSize(
                                  undefined,
                                  virtualRow.index,
                                )}
                              />
                            ) : isActiveAway ? null : (
                              <KanbanCard<TItem, TColumn>
                                item={item}
                                columnId={columnId}
                                upperNeighborId={
                                  prev ? getItemId(prev) : undefined
                                }
                                upperSortValue={
                                  prev ? getSortValue(prev) : undefined
                                }
                                lowerNeighborId={
                                  next ? getItemId(next) : undefined
                                }
                                lowerSortValue={
                                  next ? getSortValue(next) : undefined
                                }
                              />
                            )}
                          </Box>
                        );
                      })}
                    </Box>

                    {/* Destination append — a single sortable node for the
                        active card so dnd-kit can open a gap when this column
                        is the drop target. Its absolute position (parked at the
                        track bottom) is irrelevant: the committed drop reads the
                        over-card's neighbors, and the floating DragOverlay is
                        what the user actually sees follow the pointer. */}
                    {appendActive && activeItem != null && (
                      <Box
                        fill="width"
                        position="absolute" className="top-0 left-0"
                        style={{
                          transform: `translateY(${virtualizer.getTotalSize()}px)`,
                        }}
                      >
                        <KanbanCard<TItem, TColumn>
                          item={activeItem}
                          columnId={columnId}
                          dndId={activeDndId ?? undefined}
                        />
                      </Box>
                    )}
                  </Box>
                )
              )}
            </Box>
          </SortableContext>

          {/* Scroll-edge fades — pointer-inert gradient overlays toggled by the
              measured scroll edges. */}
          {canScrollUp ? (
            <Box pointerEvents="none" position="absolute"
              aria-hidden
              className="inset-x-0 top-0 h-6 bg-linear-to-b from-muted to-transparent"
            />
          ) : null}
          {canScrollDown ? (
            <Box pointerEvents="none" position="absolute"
              aria-hidden
              className="inset-x-0 bottom-0 h-6 bg-linear-to-t from-muted to-transparent"
            />
          ) : null}
        </Box>

        {/* Footer — hidden entirely when the consumer provides none; the
            column ends flush with the last card. */}
        {hasFooter ? (
          // A flex column so the footer control stretches to the column's
          // width — an inline-flex Button in a plain block box would
          // shrink-to-fit instead.
          <Box ref={footerRef} direction="column" padding="sm" shrink={false}>
            {footer}
          </Box>
        ) : null}

        {/* Drop-disabled veil — covers the whole body while a card is in flight
            so the column reads as a non-droppable target. pointer-events:none so
            it never eats the drag. */}
        {showDropDisabledOverlay ? (
          <Box pointerEvents="none"
            fill="absolute"
            zIndex={10}
            align="center"
            justify="center"
            padding="md"
          >
            {/* The column's own fill as a scrim — the veil reads as the column
                dimming, not as a grey sheet laid over the board. */}
            <Box fill="absolute" background="mutedVeil" />
            {/* Every column veils, but only the hovered one explains why —
                otherwise the message stutters across all columns at once. */}
            {isDraggingOver ? dropDisabledOverlay : null}
          </Box>
        ) : null}
      </Box>
    </Box>
  );

  return (
    <KanbanColumnItemsProvider value={getOrderedItems}>
      {expandedBody}
    </KanbanColumnItemsProvider>
  );
}

export const KanbanColumn = React.memo(
  KanbanColumnInner,
) as typeof KanbanColumnInner;
